import { Backdrop } from '@room-match/shared-ui-components';
import React, { Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { REGIONS } from 'shared/constants/REGIONS';
import { ROUTES } from 'shared/constants/ROUTES';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
const AsyncDashboard = React.lazy(() => import('pages/Dashboard'));
const AsyncSettings = React.lazy(() => import('pages/Settings'));
const AsyncProfiles = React.lazy(() => import('pages/Profiles'));
const AsyncOpportunities = React.lazy(() => import('pages/Opportunities'));
const AsyncCheckIn = React.lazy(() => import('pages/CheckIn'));
const AsyncSelfTapes = React.lazy(() => import('pages/SelfTapes'));
const AsyncMyInfo = React.lazy(() => import('pages/MyInfo'));
const AsyncMedia = React.lazy(() => import('pages/Media'));
const AsyncPastSelfTapes = React.lazy(() => import('pages/PastSelfTapes'));
const AsyncAdition = React.lazy(() => import('pages/Audition'));
const AsyncMyLinks = React.lazy(() => import('pages/MyLinks/MyLinks'));
const AsyncCalendar = React.lazy(() => import('components/Event/Event'));
const AsyncAgencyRegistration = React.lazy(() => import('pages/AgencyRegistration/AgencyRegistration'));
const AsyncUnauthenticated = React.lazy(() => import('pages/CheckIn/Unauthenticated/Unauthenticated'));
const AsyncCheckInRedirect = React.lazy(() => import('pages/CheckInRedirect/CheckInRedirect'));
const AsyncCompleteCheckin = React.lazy(() => import('pages/CompleteCheckin/CompleteCheckin'));
const AsyncCompleteST = React.lazy(() => import('pages/CompleteST/CompleteST'));
const AsyncDeleteAccount = React.lazy(() => import('pages/DeleteAccount/DeleteAccount'));
const AsyncDownload = React.lazy(() => import('pages/Download'));
const AsyncImpersonation = React.lazy(() => import('pages/Impersonation/Impersonation'));
const AsyncInitiateDelete = React.lazy(() => import('pages/InitiateDelete/InitiateDelete'));
const AsyncJobs = React.lazy(() => import('pages/Jobs/Jobs'));
const AsyncNewTalent = React.lazy(() => import('pages/NewTalent/NewTalent'));
const AsyncPublicProfile = React.lazy(() => import('pages/PublicProfile/PublicProfile'));
const AsyncForgotPassword = React.lazy(() => import('pages/ForgotPassword'));
const AsyncNotFound = React.lazy(() => import('pages/NotFound'));
const AsyncLogin = React.lazy(() => import('pages/Login'));
const AsyncRegistration = React.lazy(() => import('pages/Registration'));
const AsyncResetPassword = React.lazy(() => import('pages/ResetPassword'));
const AsyncSendVerification = React.lazy(() => import('pages/SendVerification'));
const AsyncSignup = React.lazy(() => import('pages/Signup'));
const AsyncRepresentatives = React.lazy(() => import('pages/Representatives/Representatives'));
const AsyncEditProfile = React.lazy(() => import('pages/Representatives/features/EditProfile/EditProfile'));

const Routes = () => {
  const subdomain = window.location.hostname.split('.')[0];
  const isJob = subdomain === 'jobs';
  if (isJob)
    return (
      <Suspense fallback={<Backdrop isLoading={true} />}>
        <Switch>
          <Route exact path={`/:project_id?/:role_id?`} component={AsyncJobs} />
          <Route exact path={'*'} component={() => <Redirect to={ROUTES.ROOT} />} />
        </Switch>
      </Suspense>
    );

  return (
    <Suspense fallback={<Backdrop isLoading={true} />}>
      <Switch>
        <Route exact path={ROUTES.ROOT} component={() => <Redirect to={ROUTES.LOGIN} />} />
        <PublicRoute exact path={ROUTES.LOGIN} component={AsyncLogin} />
        <PublicRoute exact path={ROUTES.SIGNUP} component={AsyncSignup} />
        <PublicRoute exact path={`${ROUTES.AGENCY_REGISTRATION}/:agency`} component={AsyncAgencyRegistration} />
        <PublicRoute exact path={`${ROUTES.INVITE_REGISTRATION}/:agency`} component={AsyncAgencyRegistration} />
        <PublicRoute exact path={`${ROUTES.UNAUTHENTICATED}/:step?`} component={AsyncUnauthenticated} />
        <PrivateRoute exact path={ROUTES.TALENT.DASHBOARD} component={AsyncDashboard} />
        <PrivateRoute exact path={`${ROUTES.TALENT.SETTINGS}/:tab`} component={AsyncSettings} />
        <PrivateRoute exact path={`${ROUTES.TALENT.PROFILES}/:profileId?/:tab?`} component={AsyncProfiles} />
        <PrivateRoute
          exact
          path={`${ROUTES.TALENT.OPPORTUNITIES}/:projectId?/:roleId?`}
          component={AsyncOpportunities}
        />
        <PrivateRoute path={ROUTES.TALENT.CALENDAR} component={AsyncCalendar} />
        <PrivateRoute exact path={`${ROUTES.TALENT.CHECKIN}/:projectId?/:sessionId?/:step?`} component={AsyncCheckIn} />
        <PrivateRoute exact path={`${ROUTES.TALENT.SELFTAPES}/:projectId?/:audId?/:step?`} component={AsyncSelfTapes} />
        <PrivateRoute exact path={`${ROUTES.TALENT.MYINFO}/:tab`} component={AsyncMyInfo} />
        <PrivateRoute exact path={`${ROUTES.TALENT.MEDIA}/:tab`} component={AsyncMedia} />
        <PrivateRoute exact path={`${ROUTES.TALENT.AUDITIONS}/:tab?/:audition_id?`} component={AsyncAdition} />
        <PrivateRoute
          exact
          path={`${ROUTES.TALENT.PAST_SELFTAPES}/:auditionScheduleId?`}
          component={AsyncPastSelfTapes}
        />
        <PrivateRoute exact path={ROUTES.TALENT.MYLINKS} component={AsyncMyLinks} />
        <PrivateRoute exact path={ROUTES.TALENT.REPRESENTATIVES} component={AsyncRepresentatives} />
        <PrivateRoute exact path={`${ROUTES.TALENT.REPRESENTATIVES}/:id/:tabs?`} component={AsyncEditProfile} />
        <Route exact path={ROUTES.DOWNLOAD} component={AsyncDownload} />
        <Route path={ROUTES.SEND_VERIFICATION} component={AsyncSendVerification} />
        <Route path={ROUTES.FORGOT_PASSWORD} component={AsyncForgotPassword} />
        <Route path={ROUTES.RESET_PASSWORD} component={AsyncResetPassword} />
        <Route path={`${ROUTES.REGISTRATION}/:adult_minor?/:step?`} component={AsyncRegistration} />
        <Route path={`${ROUTES.NEW_TALENT}/:option/:projectId`} component={AsyncNewTalent} />
        <Route path={`${ROUTES.REDIRECT}/:option/:projectId/:roleId?`} component={AsyncCheckInRedirect} />
        <Route path={`${ROUTES.TALENT.COMPLETE_CHECKIN}/:projectId/:sessionId`} component={AsyncCompleteCheckin} />
        <Route path={`${ROUTES.TALENT.COMPLETE_ST}/:projectId/:roleId`} component={AsyncCompleteST} />
        <Route path={`${ROUTES.DELETE_ACCOUNT}`} component={AsyncDeleteAccount} />
        <Route path={`${ROUTES.INITIATE_DELETE}`} component={AsyncInitiateDelete} />
        <Route path={`${ROUTES.IMPERSONATION}/:token`} component={AsyncImpersonation} />
        {REGIONS.map((r) => (
          <Route path={`/${r}/:talent_slug/:profile_id?`} component={AsyncPublicProfile} />
        ))}
        <Route path="*" exact component={AsyncNotFound} />
      </Switch>
    </Suspense>
  );
};

export default Routes;
